import { Component, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ToastrService } from "ngx-toastr";
import { Router, ActivatedRoute } from "@angular/router";
import { PasswordResetService } from "../../../services/password-reset.service";
import { UserDataStoreService } from "../../../services/user-data-store.service";
import { LoggedInUser } from "../../../models/user";
import { ConfirmationDialogComponent } from '../../../partials/confirmation-dialog/confirmation-dialog.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';


@Component({
  selector: 'app-password-reset',
  templateUrl: './password-reset.component.html',
  styleUrls: ['./password-reset.component.scss']
})
export class PasswordResetComponent implements OnInit {
  public currentUser: LoggedInUser;
  public email: string;
  public password: FormControl;
  public confirmPassword: FormControl;
  public oldPassword: FormControl;
  public rememberMe: boolean = true;
  private _destroyed$ = new Subject<any>();
  public token: string;
  public confirmFailed: boolean = false;
  public submitted = false;

  constructor(private _passwordResetService: PasswordResetService,
    private _userService: UserDataStoreService,
    private _router: Router,
    private route: ActivatedRoute,
    private _toastService: ToastrService,
    private _modalService: NgbModal) {
    route.params.pipe(takeUntil(this._destroyed$)).subscribe(async params => {
      this.token = params.token;
      this._userService.currentUser.subscribe(user => {
        if (!this.token && !!user && !!user.isActive) {
          this.currentUser = user;
          this.token = this.currentUser.token
          this.oldPassword = new FormControl('', Validators.compose([Validators.required]));
        }
      })
    });
  }


  ngOnInit() {
    this.password = new FormControl('', Validators.compose([
      Validators.required,
      Validators.minLength(8),
      Validators.pattern(/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$^+=!*()@%&])/)
    ]));

    this.confirmPassword = new FormControl('', Validators.compose([
      Validators.required,
      this.passwordValidator()
    ]));

    this.route.queryParams.pipe(takeUntil(this._destroyed$)).subscribe(params => {
      if (params['expired']) {
        this._toastService.warning(
          'Your password has expired. Please reset it before continuing.',
          'Password Expired'
        );
      }
    });
  }


  public passwordValidator(): (AbstractControl) => ValidationErrors | null {

    return (control: AbstractControl): ValidationErrors | null => {
      if (!!control.value && control.value === this.password.value) {
        return null;
      }

      return { passwordsMatch: true };
    }
  }

  public async reset(): Promise<void> {
    if (this.password.invalid || this.confirmPassword.invalid) {
      this.password.markAsTouched();
      this.confirmPassword.markAsTouched();
      this.submitted = true;
    } else {
      let confirmed;

      if (!!this.currentUser) {
        this.email = this.currentUser.email;
        confirmed = await this._passwordResetService.resetPasswordForLoggedInUser(this.password.value, this.oldPassword.value, this.email);
      } else {
        confirmed = await this._passwordResetService.resetPassword(this.password.value, this.email, this.token);
      }


      if (confirmed.success === true) {

        this._toastService.success(confirmed.message, "Success");
        if (!this.currentUser) {
          await this._router.navigate(['account/login']);
        } else {
          await this._router.navigate(['./']);
        }
      } else {
        this._toastService.error(confirmed.message, "Error");
      }
    }

  }

  public async goBack(): Promise<void> {
    const dialogRef = this._modalService.open(ConfirmationDialogComponent, {
      windowClass: 'modal-fade-in',
      centered: false,
      size: 'xl',
      backdrop: 'static'
    });
    dialogRef.componentInstance.showCancel = true;
    dialogRef.componentInstance.acceptButtonText = "Yes";
    dialogRef.componentInstance.cancelButtonText = "No"
    dialogRef.componentInstance.title = "Reset password"
    dialogRef.componentInstance.message = "Are you sure you want to cancel?"

    const result = await dialogRef.result;
    if (result) {
      this._router.navigate(['account/login']);
    }
  }

}
